import { useEffect } from 'react'
import { useLanguage, localizePath } from '../contexts/LanguageContext'

const SITE_URL = 'https://automechlabs.com/workshop_assistant'
const SITE_NAME = 'Workshop Assistant'

function setMeta(name, content, attr = 'name') {
  if (!content) return
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, name)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function setLink(selector, attrs) {
  let link = document.head.querySelector(selector)
  if (!link) {
    link = document.createElement('link')
    document.head.appendChild(link)
  }
  for (const [k, v] of Object.entries(attrs)) link.setAttribute(k, v)
}

// Greek <title>/description per route. Pages pass their English SEO text as
// props; Greek pages (/el/...) use these instead, written around the terms
// Greek shops actually search for (κοστολόγηση, λαμαρίνα, laser, στράντζα).
const EL_META = {
  '/': {
    title: 'Πρόγραμμα Κοστολόγησης Λαμαρίνας & Προσφορών',
    description: 'Κοστολόγηση λαμαρίνας σε δευτερόλεπτα: laser κοπή, στράντζα, υλικό και εργατοώρες. Εισαγωγή DXF & STEP, άμεσες προσφορές και αναφορές Excel για μεταλλουργεία.',
  },
  '/features': {
    title: 'Λειτουργίες — Κοστολόγηση, DXF, Nesting & Αναφορές',
    description: 'Υπολογιστής κόστους λαμαρίνας, εισαγωγή DXF/STEP, nesting, δημιουργία προσφορών και αναφορές Excel για κατασκευαστές μεταλλικών εξαρτημάτων.',
  },
  '/pricing': {
    title: 'Τιμές — Πακέτα για Κάθε Εργαστήριο',
    description: 'Απλές, διαφανείς τιμές για το Workshop Assistant. Συγκρίνετε τα πακέτα και βρείτε το κατάλληλο για το μεταλλουργείο ή το CNC εργαστήριό σας.',
  },
  '/support': {
    title: 'Υποστήριξη & Εγχειρίδια',
    description: 'Εγχειρίδια χρήσης, οδηγοί εγκατάστασης CAD plugins, βίντεο και συχνές ερωτήσεις για το πρόγραμμα κοστολόγησης Workshop Assistant.',
  },
  '/download': {
    title: 'Δωρεάν Λήψη Δοκιμαστικής Έκδοσης',
    description: 'Κατεβάστε δωρεάν το Workshop Assistant — χωρίς πιστωτική κάρτα. Εκδόσεις για Windows και Android με άμεση ενεργοποίηση.',
  },
  '/contact': {
    title: 'Επικοινωνία',
    description: 'Επικοινωνήστε με την ομάδα του Workshop Assistant για πωλήσεις, υποστήριξη ή ειδικές λειτουργίες. Απαντάμε συνήθως εντός 24 ωρών.',
  },
  '/about': {
    title: 'Σχετικά με Εμάς',
    description: 'Γνωρίστε το Workshop Assistant, το ελληνικό πρόγραμμα κοστολόγησης λαμαρίνας και CNC router για μεταλλουργεία και κατασκευαστές.',
  },
  '/privacy': {
    title: 'Πολιτική Απορρήτου',
    description: 'Πώς το Workshop Assistant συλλέγει, χρησιμοποιεί και προστατεύει τα προσωπικά σας δεδομένα, σύμφωνα με τον GDPR.',
  },
  '/terms': {
    title: 'Όροι Χρήσης',
    description: 'Οι όροι χρήσης του Workshop Assistant, συμπεριλαμβανομένων αδειών χρήσης, αγορών και επιστροφών χρημάτων.',
  },
  '/sheet-metal-costing': {
    title: 'Κοστολόγηση Λαμαρίνας — Πώς Υπολογίζεται το Κόστος (Οδηγός)',
    description: 'Πώς υπολογίζεται το κόστος λαμαρίνας: υλικό και φύρα, κοπή laser, στράντζα, σετάρισμα και κέρδος, με τύπο και παράδειγμα. Και πρόγραμμα που το κάνει αυτόματα.',
  },
  '/production-costing': {
    title: 'Κοστολόγηση Παραγωγής για Μεταλλικές Κατασκευές',
    description: 'Υπολογίστε το πραγματικό κόστος παραγωγής λαμαρίνας και CNC εξαρτημάτων — υλικό, χρόνος μηχανής, εργασία και περιθώριο κέρδους — αυτόματα.',
  },
  '/bend-costing': {
    title: 'Κοστολόγηση Στράντζας — Αυτόματος Εντοπισμός Στραντζαρισμάτων',
    description: 'Αυτόματος εντοπισμός γραμμών στραντζαρίσματος από αρχεία DXF/STEP και υπολογισμός κόστους στράντζας ανά εξάρτημα.',
  },
  '/laser-costing': {
    title: 'Κοστολόγηση Laser Κοπής Λαμαρίνας',
    description: 'Αυτόματος υπολογισμός κόστους κοπής laser και ψαλιδιού για λαμαρίνα, βάσει περιμέτρου, υλικού και πάχους.',
  },
  '/tube-laser-costing': {
    title: 'Κοστολόγηση Laser Κοπής Σωλήνων & Προφίλ',
    description: 'Υπολογίστε το κόστος laser κοπής μεταλλικών σωλήνων και προφίλ με την ίδια μηχανή κοστολόγησης που χρησιμοποιείτε για τη λαμαρίνα.',
  },
  '/dxf-step-import': {
    title: 'Εισαγωγή DXF & STEP για Κοστολόγηση Λαμαρίνας',
    description: 'Εισάγετε αρχεία DXF με χειροκίνητο πάχος ή STEP με αυτόματη αναγνώριση πάχους και αφήστε το Workshop Assistant να αναλύσει τη γεωμετρία.',
  },
  '/solidworks-plugin': {
    title: 'Πρόσθετο SolidWorks — SheetMetalToJSON',
    description: 'Εξαγωγή δεδομένων λαμαρίνας απευθείας από το SolidWorks στο Workshop Assistant με το πρόσθετο SheetMetalToJSON.',
  },
  '/inventor-plugin': {
    title: 'Πρόσθετο Autodesk Inventor — SheetMetalToJSON',
    description: 'Εξαγωγή δεδομένων λαμαρίνας απευθείας από το Autodesk Inventor στο Workshop Assistant με το πρόσθετο SheetMetalToJSON.',
  },
  '/quotation-software': {
    title: 'Πρόγραμμα Προσφορών & Προτιμολογίων',
    description: 'Δημιουργήστε προσφορές, διαχειριστείτε παραγγελίες και εκδώστε προτιμολόγια για εργασίες λαμαρίνας με το Workshop Assistant.',
  },
  '/manufacturing-cost-estimator': {
    title: 'Πρόγραμμα Κοστολόγησης & Εκτίμησης Κόστους Κατασκευής',
    description: 'Πρόγραμμα κοστολόγησης για μεταλλουργεία και εργαστήρια CNC: κόστος λαμαρίνας, σωλήνα και φρέζας από αρχεία DXF/STEP και προσφορές σε λεπτά. Δωρεάν δοκιμή.',
  },
  '/metal-parts-costing': {
    title: 'Κοστολόγηση Μεταλλικών Εξαρτημάτων',
    description: 'Υπολογίστε υλικό, κοπή laser, στράντζα, σετάρισμα και κέρδος για εξαρτήματα από χάλυβα, ανοξείδωτο και αλουμίνιο, απευθείας από DXF/STEP.',
  },
  '/cnc-milling-costing': {
    title: 'Κοστολόγηση Φρέζας CNC — Πρόγραμμα Κοστολόγησης Φρεζαρίσματος',
    description: 'Κοστολόγηση φρέζας CNC router: χρόνος μηχανής, φθορά εργαλείου, βυθίσεις, υλικό φύλλου και εργατικά από αρχεία DXF/STEP, με προσφορά Excel.',
  },
  '/cost-calculator': {
    title: 'Δωρεάν Υπολογιστής Κόστους Λαμαρίνας',
    description: 'Δωρεάν υπολογιστής κόστους για εξαρτήματα λαμαρίνας: υλικό, κοπή, στράντζα, σετάρισμα και τιμή πώλησης ανά τεμάχιο, online χωρίς εγγραφή.',
  },
}

function setFaqJsonLd(faq) {
  const existing = document.head.querySelector('#seo-faq-jsonld')
  if (!faq || faq.length === 0) {
    if (existing) existing.remove()
    return
  }
  let script = existing
  if (!script) {
    script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'seo-faq-jsonld'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  })
}

// Updates document title, meta description, canonical URL, Open Graph/Twitter
// tags, and (optionally) FAQPage JSON-LD per route. Google renders JS and
// picks these up; crawlers that don't run JS (most social-share bots) fall
// back to the static defaults in index.html.
const SEO = ({ title: titleEn, description: descriptionEn, path = '/', faq }) => {
  const { lang } = useLanguage()
  const el = lang === 'el' ? EL_META[path] : null
  const title = el?.title || titleEn
  const description = el?.description || descriptionEn

  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`
    const enUrl = `${SITE_URL}${path}`
    const elUrl = `${SITE_URL}${localizePath(path, 'el')}`
    const url = lang === 'el' ? elUrl : enUrl

    document.title = fullTitle
    document.documentElement.lang = lang
    setMeta('description', description)
    setLink('link[rel="canonical"]', { rel: 'canonical', href: url })
    setLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en', href: enUrl })
    setLink('link[rel="alternate"][hreflang="el"]', { rel: 'alternate', hreflang: 'el', href: elUrl })
    setLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: enUrl })
    setMeta('og:locale', lang === 'el' ? 'el_GR' : 'en_US', 'property')
    setMeta('og:title', fullTitle, 'property')
    setMeta('og:description', description, 'property')
    setMeta('og:url', url, 'property')
    setMeta('twitter:title', fullTitle)
    setMeta('twitter:description', description)
    setFaqJsonLd(faq)

    return () => setFaqJsonLd(null)
  }, [title, description, path, faq, lang])

  return null
}

export default SEO
